import {
  Component,
  input,
  effect,
  signal,
  computed,
  inject,
  untracked,
  OnInit,
  OnDestroy,
  DestroyRef,
  HostBinding,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { firstValueFrom, of } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzModalModule, NzModalComponent } from 'ng-zorro-antd/modal';
import { NzDrawerModule, NzDrawerComponent } from 'ng-zorro-antd/drawer';
import { NzSplitterModule } from 'ng-zorro-antd/splitter';

import {
  ProjectDataService,
  ProjectDataActions,
  ModelMutationDescriptor,
  selectProjectModelsList,
  matchPageEntitiesWithMutations,
} from '@rapider/project-data';
import {
  BrowserStorageManagementService,
  NotificationService,
} from '@rapider/services';
import { TemplateRendererComponent } from '../template-renderer/template-renderer.component';

import { MonacoCodeEditorComponent } from '@rapider/monaco-code-editor';
import {
  DefinitionControllerService,
  FileManagementControllerService,
} from '@rapider/sdk';
import { AuthStore } from '@rapider/authentication';

import {
  PageConfig,
  DataVariable,
  AppEvent,
  applyDataTransformations,
  interpolateObjectWithContext,
  interpolateStringWithContext,
  buildFilterPath,
  buildClientLocalStatePath,
  formatPageConfigForDisplay,
  DYNAMIC_PAGE_CONFIG,
} from './dynamic-page-renderer.config';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { DynamicPageUiService } from '../dynamic-page-ui.service';
import { isTrustedAppResourceUrl } from '../security/sandbox-security.util';

/**
 * @class DynamicPageRendererComponent
 * @description A powerful execution engine that renders fully dynamic, data-driven applications
 * directly from JSON AI configurations. Handles API orchestration, declarative transformations via JSONata,
 * secure template compilation, and native event delegation binding. Includes a 3D flip card view
 * to inspect and edit the underlying HTML Template, JSON Data, and Page Config dynamically.
 */
@Component({
  selector: 'rapider-dynamic-page-renderer',
  standalone: true,
  imports: [
    CommonModule,
    TemplateRendererComponent,
    NzTabsModule,
    MonacoCodeEditorComponent,
    NzModalModule,
    NzDrawerModule,
    NzSplitterModule,
  ],
  templateUrl: './dynamic-page-renderer.component.html',
})
export class DynamicPageRendererComponent implements OnInit, OnDestroy {
  // Use dynamic HostBindings so the component can expand vertically when required by the Interactive Review mode
  @HostBinding('class.block') readonly block = true;
  @HostBinding('class.w-full') readonly wFull = true;
  @HostBinding('class.h-full') get isFullHeight() {
    return !this.expandToContentHeight();
  }
  @HostBinding('class.h-auto') get isAutoHeight() {
    return this.expandToContentHeight();
  }

  @ViewChild('pageDrawer', { static: false })
  pageDrawerComponent?: NzDrawerComponent;
  @ViewChild('pageModal', { static: false })
  pageModalComponent?: NzModalComponent;

  private readonly projectDataService = inject(ProjectDataService);
  private readonly browserStorage = inject(BrowserStorageManagementService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  public readonly dynamicPageUiService = inject(DynamicPageUiService);
  private readonly el = inject(ElementRef); // Inject ElementRef to scope DOM queries to this component instance
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly fileManagementController = inject(
    FileManagementControllerService,
  );
  private readonly authStore = inject(AuthStore);
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspaceModels = this.store.selectSignal(
    selectProjectModelsList,
  );

  public readonly configTexts = DYNAMIC_PAGE_CONFIG.texts;

  /** Input Signal: The complete PageConfig object containing variables, expressions, templates, and events. */
  public pageConfig = input.required<PageConfig>();

  /** Input Signal: Initial payload to merge with root data (e.g. from router query params or modal arguments). */
  public initialPayload = input<Record<string, any>>({});

  /** Input Signal: Optional list of pre-resolved manifest pages (for installed apps). */
  public manifestPages = input<any[]>([]);

  /** Optional Input Signal: ID of the parent App definition */
  public appId = input<string>();

  /** Optional Input Signal: App definition object or manifest containing webPages / pages */
  public appDefinition = input<any>();

  /** Input Signal: Flags if the component should break out of absolute positioning to push parent height. */
  public expandToContentHeight = input<boolean>(false);

  /** Input Signal: Target view mode used to automatically strip media queries for simulations. */
  public simulateViewMode = input<'desktop' | 'tablet' | 'mobile'>('desktop');

  /** Input Signal: Disables external UI control bindings for when rendered inside embedded previews */
  public standaloneMode = input<boolean>(false);

  /** Output Signal: Emits the current state context (pageState and rootData) whenever it mutates natively. */
  public onStateChange = output<Record<string, any>>();

  /** Output Signal: Emits runtime execution events and payloads for debugging observability. */
  public onEventTriggered = output<{
    eventName: string;
    context?: any;
    timestamp: Date;
    type: string;
    error?: string;
    response?: any;
  }>();

  /** Internal Signals for State Management */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public rootDataObject = signal<Record<string, any>>({});

  /** Tracks dynamic state context based on user inputs or workflow events mapped directly to keys */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public pageState = signal<Record<string, any>>({});

  public isLoading = signal<boolean>(true);

  /** Tracks background refreshing to avoid wiping the DOM out into a full-page spinner */
  public isRefreshing = signal<boolean>(false);

  public error = signal<string | null>(null);

  public activeTemplate = signal<string>('');

  // --- PAGE MODAL STATE ---
  public isPageModalVisible = signal<boolean>(false);
  public isPageModalLoading = signal<boolean>(false);
  public pageModalConfig = signal<PageConfig | null>(null);
  public pageModalPayload = signal<Record<string, any>>({});
  public pageModalTitle = signal<string>('Modal');
  public pageModalWidth = signal<number | string>(800);

  // --- PAGE DRAWER STATE ---
  public isPageDrawerVisible = signal<boolean>(false);
  public isPageDrawerLoading = signal<boolean>(false);
  public pageDrawerConfig = signal<PageConfig | null>(null);
  public pageDrawerPayload = signal<Record<string, any>>({});
  public pageDrawerTitle = signal<string>('Drawer');
  public pageDrawerPlacement = signal<'left' | 'right' | 'top' | 'bottom'>(
    'right',
  );
  public pageDrawerSize = signal<'default' | 'large'>('large');
  public pageDrawerWidth = signal<number | string | undefined>(undefined);

  // --- PAGE SPLITTER STATE ---
  public isPageSplitterVisible = signal<boolean>(false);
  public isPageSplitterLoading = signal<boolean>(false);
  public pageSplitterConfig = signal<PageConfig | null>(null);
  public pageSplitterPayload = signal<Record<string, any>>({});
  public pageSplitterTitle = signal<string | undefined>(undefined);
  public pageSplitterIcon = signal<string | undefined>(undefined);
  public pageSplitterDefaultSize = signal<number | string>('30%');
  public pageSplitterMin = signal<number | string | undefined>(undefined);
  public pageSplitterMax = signal<number | string | undefined>(undefined);
  public pageSplitterResizable = signal<boolean>(true);
  public pageSplitterLayout = signal<'horizontal' | 'vertical'>('horizontal');

  /** * Computed: Resolves the exact drawer width to prevent template structural re-renders.
   * Maps NG-Zorro's native size strings to their pixel equivalents if no custom width is provided.
   * 'large' = 736px, 'default' = 378px.
   */
  public effectiveDrawerWidth = computed(() => {
    const w = this.pageDrawerWidth();
    if (w !== undefined && w !== null && w !== '') return w;
    return this.pageDrawerSize() === 'large' ? 736 : 378;
  });

  /** Computed: Merges the root API data and the local pageState so the Handlebars template can read both seamlessly */
  public templateContext = computed(() => {
    return {
      ...this.rootDataObject(),
      pageState: this.pageState(),
    };
  });

  // Computed signals to safely stringify objects for Monaco Editor consumption
  public templateContextStringified = computed(() =>
    JSON.stringify(this.templateContext(), null, 2),
  );

  // CRITICAL FIX: Leverage pure function to strip the raw HTML template out of the Config JSON display
  public pageConfigStringified = computed(() =>
    JSON.stringify(formatPageConfigForDisplay(this.pageConfig()), null, 2),
  );

  constructor() {
    // Automatically trigger the data pipeline when the config input stabilizes
    effect(() => {
      const config = this.pageConfig();
      if (config) {
        untracked(() => {
          this.activeTemplate.set(config.template || '');
          this.loadPipeline();
        });
      }
    });

    // Automatically emit native state mutations for debugging observability
    effect(() => {
      const currentState = this.pageState();
      const rootData = this.rootDataObject();
      untracked(() => {
        this.onStateChange.emit({ pageState: currentState, rootData });
      });
    });

    const pendingMutations: ModelMutationDescriptor[] = [];
    this.actions$
      .pipe(
        ofType(ProjectDataActions.notifyModelMutations),
        tap(({ mutations }) => {
          if (Array.isArray(mutations)) {
            pendingMutations.push(...mutations);
          }
        }),
        debounceTime(250),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const batch = pendingMutations.splice(0, pendingMutations.length);
        if (batch.length > 0) {
          this.handleAiModelMutations(batch);
        }
      });
  }

  private handleAiModelMutations(mutations: ModelMutationDescriptor[]): void {
    if (this.isLoading()) return;
    const config = this.pageConfig();
    const vars = config?.dataVariables || [];
    if (vars.length === 0) return;

    const allModels = [
      ...(this.appDefinition()?.dataModels || []),
      ...(this.workspaceModels() || []),
    ];
    const varsToRefresh: string[] = [];

    for (const v of vars) {
      if (v.type !== 'endpoint' || !v.payload?.http?.path) continue;
      const rawPath = v.payload.http.path.split('?')[0];
      const dmMatch = rawPath.match(/\/project-data-manager\/([^/?#]+)/i);
      const pmMatch = rawPath.match(/\/project-models\/([^/?#]+)\/items/i);
      const segments = rawPath.split('/').filter(Boolean);
      const candidateToken =
        dmMatch?.[1] || pmMatch?.[1] || segments[0] || '';
      if (!candidateToken) continue;

      const matched = matchPageEntitiesWithMutations(
        [candidateToken],
        mutations,
        allModels,
      );
      if (matched.length > 0) {
        varsToRefresh.push(v.variableName);
      }
    }

    if (varsToRefresh.length > 0) {
      this.refreshVariables(varsToRefresh);
    }
  }

  ngOnInit(): void {
    if (!this.standaloneMode()) {
      // Notify the global layout that a dynamic page is active so it displays the header toggles
      this.dynamicPageUiService.isDynamicPageActive.set(true);
      this.dynamicPageUiService.isCodeViewActive.set(false);
    }
  }

  ngOnDestroy(): void {
    if (!this.standaloneMode()) {
      // Cleanup: Hide the header toggles when navigating away from the dynamic app
      this.dynamicPageUiService.isDynamicPageActive.set(false);
    }
  }

  /**
   * Captures changes made to the HTML in the Monaco Editor and updates the active renderer template.
   * @param {string} newTemplate - The updated HTML string.
   */
  public handleTemplateChange(newTemplate: string): void {
    this.activeTemplate.set(newTemplate);
  }

  /**
   * Captures changes made to the JSON data context in the Monaco Editor.
   * Parses and applies valid JSON to immediately reflect within the Handlebars template.
   * @param {string} newData - The stringified JSON data payload.
   */
  public handleDataChange(newData: string): void {
    try {
      const parsed = JSON.parse(newData);
      // If the user modified the pageState block natively in the editor, split it out safely
      if (parsed.pageState) {
        this.pageState.set(parsed.pageState);
        delete parsed.pageState;
      }
      this.rootDataObject.set(parsed);
    } catch (e) {
      // Silently ignore parse errors while the user is actively typing
    }
  }

  /**
   * Captures changes made to the overall PageConfig JSON in the Monaco Editor.
   * (Currently handles local read-only display or console logs, saving to DB requires explicit parent events).
   * @param {string} newConfig - The stringified PageConfig payload.
   */
  public handlePageConfigChange(newConfig: string): void {
    try {
      const parsed = JSON.parse(newConfig);
      console.log('[DynamicPageRenderer] Page Config preview updated:', parsed);
    } catch (e) {
      // Silently ignore parse errors
    }
  }

  /**
   * Orchestrates the 3-step pipeline: Fetch Data -> Transform Data -> Render
   * Fetches all designated dataVariables in parallel to reduce load times, then applies transformations sequentially.
   */
  public async loadPipeline(): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const config = this.pageConfig();
      const currentData: Record<string, any> = {};

      // Inject the initial payload provided via inputs (e.g. queryParams or modal payload) into the starting context
      const initPayload = this.initialPayload();
      if (initPayload && Object.keys(initPayload).length > 0) {
        Object.assign(currentData, initPayload);
      }

      // 1. Fetch all variables concurrently
      const fetchPromises = (config.dataVariables || []).map((v) =>
        this.fetchVariable(v),
      );
      const fetchResults = await Promise.all(fetchPromises);

      for (const res of fetchResults) {
        currentData[res.key] = res.value;
      }

      // 2. Apply transformations sequentially.
      // Inject pageState so inputs can act as filters natively.
      const transformationContext = {
        ...currentData,
        pageState: this.pageState(),
      };
      const finalData = await applyDataTransformations(
        config.dataTransformations || [],
        transformationContext,
      );

      // Clean up to prevent nested duplication in rootDataObject
      delete finalData['pageState'];

      // 3. Commit state to trigger the underlying TemplateRendererComponent
      this.rootDataObject.set(finalData);
    } catch (err: any) {
      console.error('[DynamicPageRenderer] Pipeline Error:', err);
      this.error.set(err.message || this.configTexts.defaultError);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Resolves a single DataVariable definition by fetching from the designated source.
   * Maps endpoints, static values, local storage, and server-side client states robustly.
   * @param {DataVariable} variable - The configuration declaring the source, keys, and endpoints.
   * @returns {Promise<{ key: string, value: any }>} Key-value pair resolving the targeted variable.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async fetchVariable(
    variable: DataVariable,
  ): Promise<{ key: string; value: any }> {
    if (variable.type === 'static') {
      return { key: variable.variableName, value: variable.payload.value };
    }

    if (variable.type === 'localStorage') {
      const storageKey = variable.payload.key || variable.variableName;
      const val = this.browserStorage.getItem(storageKey);
      return { key: variable.variableName, value: val };
    }

    if (variable.type === 'clientLocalState') {
      const key = variable.payload.key || variable.variableName;
      const path = buildClientLocalStatePath(key);
      try {
        const response = await firstValueFrom(
          this.projectDataService.executeProjectApiCall('GET', path, null),
        );
        // Extracts the inner value correctly if multiple records match or the payload is raw
        const val =
          Array.isArray(response) && response.length > 0
            ? response[0].value
            : null;
        return { key: variable.variableName, value: val };
      } catch (e) {
        console.warn(
          `[DynamicPageRenderer] Failed to fetch clientLocalState for key: ${key}`,
        );
        return { key: variable.variableName, value: null };
      }
    }

    if (variable.type === 'endpoint' && variable.payload.http) {
      const http = variable.payload.http;
      const path = buildFilterPath(http.path, http.params);

      const response = await firstValueFrom(
        this.projectDataService.executeProjectApiCall(
          http.method,
          path,
          http.body,
        ),
      );

      return { key: variable.variableName, value: response };
    }

    return { key: variable.variableName, value: null };
  }

  /**
   * Refetches specific data variables natively and recalculates transformations without wiping unrelated state.
   * Uses isRefreshing to seamlessly swap DOM elements without flashing a full loading spinner.
   * @param {string[]} variablesToRefresh - Optional array limiting exactly which keys to refresh.
   */
  private async refreshVariables(variablesToRefresh?: string[]): Promise<void> {
    this.isRefreshing.set(true); // <--- Triggers background indicator instead of main loader

    try {
      const config = this.pageConfig();
      const currentData = { ...this.rootDataObject() };

      const varsToFetch = (config.dataVariables || []).filter(
        (v) =>
          !variablesToRefresh ||
          variablesToRefresh.length === 0 ||
          variablesToRefresh.includes(v.variableName),
      );

      // Concurrently fetch the targeted variables
      const fetchPromises = varsToFetch.map((v) => this.fetchVariable(v));
      const results = await Promise.all(fetchPromises);

      // Overlay the fresh data on top of the old state
      for (const res of results) {
        currentData[res.key] = res.value;
      }

      // Re-apply ALL transformations since they might depend on the newly fetched data
      const transformationContext = {
        ...currentData,
        pageState: this.pageState(),
      };
      const finalData = await applyDataTransformations(
        config.dataTransformations || [],
        transformationContext,
      );
      delete finalData['pageState'];

      this.rootDataObject.set(finalData);
    } catch (err: any) {
      console.error('[DynamicPageRenderer] Refresh Error:', err);
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to refresh data.',
      );
    } finally {
      this.isRefreshing.set(false); // <--- Clears background indicator
    }
  }

  /**
   * Core Engine Pipeline:
   * Re-evaluates all JSONata dataTransformations natively against the active memory state.
   * This enables high-performance local UI filtering, sorting, and map calculations
   * without executing heavy or redundant HTTP network requests.
   */
  private async recalculateTransformations(): Promise<void> {
    const config = this.pageConfig();
    if (!config.dataTransformations || config.dataTransformations.length === 0)
      return;

    try {
      // Synthesize the evaluation context by combining fetched API data and live user inputs
      const transformationContext = {
        ...this.rootDataObject(),
        pageState: this.pageState(),
      };

      const finalData = await applyDataTransformations(
        config.dataTransformations,
        transformationContext,
      );

      // Strip pageState to prevent recursive nesting in the root data object
      delete finalData['pageState'];

      // Setting rootDataObject automatically triggers the Handlebars re-render
      this.rootDataObject.set(finalData);
    } catch (error) {
      console.error(
        '[DynamicPageRenderer] Local transformation recalculation failed:',
        error,
      );
    }
  }

  /**
   * Input Interaction Listener bound via Event Delegation.
   * Synchronizes data dynamically to the local `pageState` object from inputs carrying a `data-state-key`.
   * Triggers on both `input` (real-time typing) and `change` (blur/selects).
   * @param {Event} event - The triggered DOM change/input event.
   */
  public handleInputInteraction(event: Event): void {
    const target = event.target as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement;
    if (!target) return;

    const stateKey = target.getAttribute('data-state-key');
    if (stateKey) {
      let value = target.value;

      if (target.type === 'checkbox') {
        value = (target as HTMLInputElement).checked ? target.value : '';
      }

      this.pageState.update((state) => ({ ...state, [stateKey]: value }));

      // Defer DOM rebuild slightly to allow native input events to resolve cleanly
      setTimeout(() => {
        this.recalculateTransformations();
      }, 10);
    }
  }

  /**
   * Catches `state-change` events dispatched natively by our Angular Custom Elements
   * (e.g. <rapider-select>). Updates the pageState identically to native inputs.
   */
  public handleCustomElementStateChange(event: Event): void {
    const customEvent = event as CustomEvent;
    if (customEvent && customEvent.detail) {
      const { key, value } = customEvent.detail;
      if (key) {
        // CRITICAL FIX: Because `pageState` is a Signal, updating it instantly triggers
        // a full synchronous DOM replacement by the Handlebars TemplateRenderer.
        // We MUST defer the state update itself. Giving it 200ms allows the NG-Zorro
        // CDK Overlay to gracefully close its dropdown animation before the anchor is destroyed.
        setTimeout(() => {
          this.pageState.update((state) => ({ ...state, [key]: value }));
          this.recalculateTransformations();
        }, 10);
      }
    }
  }

  /**
   * Helper to locate target page metadata from candidate list of pages.
   */
  private findPageDefinition(routeKey: string, candidatePages: any[]): any {
    if (!Array.isArray(candidatePages) || candidatePages.length === 0) {
      return null;
    }
    const cleanKey = routeKey.trim();
    const lowerKey = cleanKey.toLowerCase();
    const cleanDash = cleanKey.replace(/\//g, '-');
    const lowerDash = cleanDash.toLowerCase();

    return candidatePages.find((p: any) => {
      if (!p) return false;
      const pRoute = (p.route || p.value?.route || '').trim();
      const pKey = (p.key || p.value?.key || '').trim();
      const pId = (p.id || p._id || '').trim();
      const pName = (p.name || p.value?.name || '').trim().toLowerCase();
      const pFile = (p.htmlFilePath || p.value?.htmlFilePath || '').trim();

      return (
        pRoute === cleanKey ||
        pRoute === cleanDash ||
        pKey === cleanKey ||
        pKey === cleanDash ||
        pId === cleanKey ||
        pName === lowerKey ||
        pName === lowerDash ||
        pFile === cleanKey ||
        pFile === `pages/${cleanKey}.html` ||
        pFile === `pages/${cleanDash}.html` ||
        pFile === `${cleanKey}.html` ||
        pFile === `${cleanDash}.html`
      );
    });
  }

  /**
   * Resolves PageConfig and HTML template for a target page
   * by reading the manifest's page definition, resolving public URLs, S3 presigned URLs, develop build URLs, or definition file versions.
   */
  private async resolvePageConfig(routeKey: string): Promise<PageConfig> {
    // 1. Check manifestPages input first
    let targetPage = this.findPageDefinition(routeKey, this.manifestPages());

    // 2. Check appDefinition input
    let appDef = this.appDefinition();
    if (!targetPage && appDef) {
      const defPages =
        appDef.value?.webPages ||
        appDef.webPages ||
        appDef.value?.pages ||
        appDef.pages ||
        [];
      targetPage = this.findPageDefinition(routeKey, defPages);
    }

    // 3. If still not found and appId is provided, fetch App definition
    const effectiveAppId = this.appId() || appDef?.id || appDef?._id;
    if (!targetPage && effectiveAppId) {
      try {
        const fetchedDef = await firstValueFrom(
          this.definitionController.findById({ id: effectiveAppId }),
        );
        if (fetchedDef) {
          appDef = fetchedDef;
          const defObj = fetchedDef as any;
          const defPages =
            defObj.value?.webPages ||
            defObj.webPages ||
            defObj.value?.pages ||
            defObj.pages ||
            [];
          targetPage = this.findPageDefinition(routeKey, defPages);
        }
      } catch (err) {
        console.warn(
          '[DynamicPageRenderer] Failed to fetch App definition by ID:',
          effectiveAppId,
          err,
        );
      }
    }

    // 4. Fallback: check project applications
    if (!targetPage) {
      let projectId = this.authStore.activeProject()?.id;
      if (!projectId) {
        projectId =
          this.browserStorage.getItem<string>('activeProjectId') || undefined;
      }
      if (projectId) {
        try {
          const apps = await firstValueFrom(
            this.projectDataService.getProjectApplications(projectId),
          );
          if (Array.isArray(apps)) {
            for (const app of apps) {
              const appPages =
                app.manifest?.pages ||
                app.value?.webPages ||
                app.value?.pages ||
                app.webPages ||
                [];
              const matched = this.findPageDefinition(routeKey, appPages);
              if (matched) {
                targetPage = matched;
                if (!appDef) appDef = app;
                break;
              }
            }
          }
        } catch (err) {
          console.warn(
            '[DynamicPageRenderer] Failed to query project applications:',
            err,
          );
        }
      }
    }

    if (!targetPage) {
      throw new Error(
        `Could not find a page definition matching route: ${routeKey}`,
      );
    }

    // Resolve HTML template content
    let templateHtml = '';

    // A. Inline template
    if (
      targetPage.template ||
      targetPage.pageTemplateContent ||
      targetPage.value?.template
    ) {
      templateHtml =
        targetPage.template ||
        targetPage.pageTemplateContent ||
        targetPage.value?.template ||
        '';
    }

    // B. Public / Direct URL (for versioned apps)
    const directUrl =
      targetPage.publicUrl ||
      targetPage.htmlUrl ||
      targetPage.url ||
      targetPage.value?.publicUrl ||
      targetPage.value?.htmlUrl ||
      targetPage.value?.url;

    if (!templateHtml && directUrl && isTrustedAppResourceUrl(directUrl)) {
      try {
        templateHtml = await firstValueFrom(
          this.httpClient.get(directUrl, { responseType: 'text' }),
        );
      } catch (err) {
        console.error(
          '[DynamicPageRenderer] Failed to load HTML from public URL:',
          directUrl,
          err,
        );
      }
    }

    // C. S3 Presigned URL (for develop build on S3)
    const s3Path =
      targetPage.s3Path ||
      targetPage.value?.s3Path ||
      (appDef?.value?.developBuild?.s3Path && targetPage.htmlFilePath
        ? `${appDef.value.developBuild.s3Path}/${targetPage.htmlFilePath}`
        : null);

    if (!templateHtml && s3Path) {
      try {
        const cleanS3Path = s3Path.replace(/^s3:\/\/[^/]+\//, '');
        const res = await firstValueFrom(
          this.fileManagementController.getPrivateDownloadUrlByPath({
            path: cleanS3Path,
          }),
        );
        if (res?.downloadUrl && isTrustedAppResourceUrl(res.downloadUrl)) {
          templateHtml = await firstValueFrom(
            this.httpClient.get(res.downloadUrl, { responseType: 'text' }),
          );
        }
      } catch (err) {
        console.error(
          '[DynamicPageRenderer] Failed to load HTML from presigned S3 URL:',
          s3Path,
          err,
        );
      }
    }

    // D. Develop build manifest / CDN URL
    if (!templateHtml && appDef?.value?.developBuild?.manifestUrl) {
      const manifestUrl =
        appDef.value.developBuild.rawManifestUrl ||
        appDef.value.developBuild.manifestUrl;
      const lastSlash = manifestUrl.lastIndexOf('/');
      if (lastSlash !== -1 && targetPage.htmlFilePath) {
        const pageCdnUrl = `${manifestUrl.substring(0, lastSlash)}/${targetPage.htmlFilePath}`;
        if (isTrustedAppResourceUrl(pageCdnUrl)) {
          try {
            templateHtml = await firstValueFrom(
              this.httpClient.get(pageCdnUrl, { responseType: 'text' }),
            );
          } catch (err) {
            console.warn(
              '[DynamicPageRenderer] Failed to load HTML from develop build CDN URL:',
              pageCdnUrl,
              err,
            );
          }
        }
      }
    }

    // E. Read file version via SDK (GitHub/develop branch source in definition)
    if (!templateHtml && effectiveAppId) {
      let fileName =
        targetPage.htmlFilePath ||
        targetPage.route ||
        targetPage.key ||
        routeKey;
      if (fileName.toLowerCase().endsWith('.html')) {
        fileName = fileName.substring(0, fileName.length - 5);
      }
      try {
        templateHtml = await firstValueFrom(
          this.definitionController.readFileVersion({
            id: effectiveAppId,
            name: fileName,
            extension: 'html' as any,
          }),
        );
      } catch (err) {
        console.warn(
          '[DynamicPageRenderer] Failed to read file version from definition:',
          fileName,
          err,
        );
      }
    }

    return {
      pageId: targetPage.id || targetPage.key || routeKey,
      pageName: targetPage.value?.name || targetPage.name || routeKey,
      route: targetPage.value?.route || targetPage.route || routeKey,
      dataVariables:
        targetPage.value?.dataVariables || targetPage.dataVariables || [],
      dataTransformations:
        targetPage.value?.dataTransformations ||
        targetPage.dataTransformations ||
        [],
      events: targetPage.value?.events || targetPage.events || [],
      template: templateHtml || '',
    };
  }

  /**
   * Opens an inner page modal, dynamically resolving the requested page definition by its route,
   * fetching its template from URL, S3, or definition, and supplying the dynamic payload to its engine.
   * @param routeKey The key or route mapping to the target AppPage
   * @param payload Dynamic contextual payload object
   * @param title Title of the modal
   * @param width Width of the modal
   */
  private openPageModal(
    routeKey: string,
    payload: any,
    title?: string,
    width?: number | string,
  ): void {
    this.pageModalTitle.set(title || 'Modal View');
    this.pageModalWidth.set(width || 800);
    this.pageModalPayload.set(payload || {});

    // CRITICAL FIX: Defer visibility changes to the next event loop tick.
    // This ensures structural bindings (like width, size, and placement) are fully evaluated
    // by Angular before the overlay attempts to animate open, preventing NG0100 errors.
    setTimeout(() => {
      this.isPageModalLoading.set(true);
      this.isPageModalVisible.set(true);
    }, 0);

    const loadContent = () => {
      this.resolvePageConfig(routeKey)
        .then((pageConfig) => {
          this.pageModalConfig.set(pageConfig);
          this.isPageModalLoading.set(false);
        })
        .catch((err) => {
          this.notificationService.createNotification(
            'error',
            'Error',
            err?.message || `Failed to load modal page: ${routeKey}`,
          );
          this.isPageModalLoading.set(false);
        });
    };

    if (this.pageModalComponent) {
      const sub = this.pageModalComponent.afterOpen.subscribe(() => {
        if (sub) {
          sub.unsubscribe();
        }
        loadContent();
      });
    } else {
      setTimeout(() => loadContent(), 0);
    }
  }

  public closePageModal(): void {
    this.isPageModalVisible.set(false);
    this.pageModalConfig.set(null);
  }

  /**
   * Opens an inner page drawer, dynamically resolving the requested page definition by its route,
   * fetching its template from URL, S3, or definition, and supplying the dynamic payload to its engine.
   * @param routeKey The key or route mapping to the target AppPage
   * @param payload Dynamic contextual payload object
   * @param title Title of the drawer
   * @param placement The direction the drawer opens from (left, right, top, bottom)
   * @param size The default size constraint (default, large)
   * @param width Explicit pixel/percentage width override
   */
  private openPageDrawer(
    routeKey: string,
    payload: any,
    title?: string,
    placement?: 'left' | 'right' | 'top' | 'bottom',
    size?: 'default' | 'large',
    width?: number | string,
  ): void {
    this.pageDrawerTitle.set(title || 'Drawer View');
    this.pageDrawerPlacement.set(placement || 'right');
    this.pageDrawerSize.set(size || 'large');
    this.pageDrawerWidth.set(width);
    this.pageDrawerPayload.set(payload || {});

    // CRITICAL FIX: Defer visibility changes to the next event loop tick.
    // This ensures structural bindings (like width, size, and placement) are fully evaluated
    // by Angular before the overlay attempts to animate open, preventing NG0100 errors.
    setTimeout(() => {
      this.isPageDrawerLoading.set(true);
      this.isPageDrawerVisible.set(true);
    }, 0);

    const loadContent = () => {
      this.resolvePageConfig(routeKey)
        .then((pageConfig) => {
          this.pageDrawerConfig.set(pageConfig);
          this.isPageDrawerLoading.set(false);
        })
        .catch((err) => {
          this.notificationService.createNotification(
            'error',
            'Error',
            err?.message || `Failed to load drawer page: ${routeKey}`,
          );
          this.isPageDrawerLoading.set(false);
        });
    };

    if (this.pageDrawerComponent) {
      const sub = this.pageDrawerComponent.afterOpen.subscribe(() => {
        if (sub) {
          sub.unsubscribe();
        }
        loadContent();
      });
    } else {
      setTimeout(() => loadContent(), 0);
    }
  }

  public closePageDrawer(): void {
    this.isPageDrawerVisible.set(false);
    this.pageDrawerConfig.set(null);
  }

  /**
   * Opens an inner page splitter pane, dynamically resolving the requested page definition by its route.
   * @param routeKey The key or route mapping to the target AppPage
   * @param payload Dynamic contextual payload object
   * @param title Title of the splitter pane
   * @param icon FontAwesome icon class
   * @param defaultSize Starting size (e.g. '30%', 400)
   * @param min Minimum resizable size
   * @param max Maximum resizable size
   * @param resizable Controls if the splitter drag bar is active
   * @param layout 'horizontal' (columns) or 'vertical' (rows)
   */
  private openPageSplitter(
    routeKey: string,
    payload: any,
    title?: string,
    icon?: string,
    defaultSize?: number | string,
    min?: number | string,
    max?: number | string,
    resizable?: boolean,
    layout?: 'horizontal' | 'vertical',
  ): void {
    this.pageSplitterTitle.set(title);
    this.pageSplitterIcon.set(icon);
    this.pageSplitterDefaultSize.set(defaultSize ?? '30%');

    this.pageSplitterMin.set(
      min !== undefined && min !== null ? min : undefined,
    );
    this.pageSplitterMax.set(
      max !== undefined && max !== null ? max : undefined,
    );

    this.pageSplitterResizable.set(resizable ?? true);
    this.pageSplitterLayout.set(layout || 'horizontal');
    this.pageSplitterPayload.set(payload || {});

    this.isPageSplitterVisible.set(true);
    this.isPageSplitterLoading.set(true);

    this.resolvePageConfig(routeKey)
      .then((pageConfig) => {
        this.pageSplitterConfig.set(pageConfig);
        this.isPageSplitterLoading.set(false);
      })
      .catch((err) => {
        this.notificationService.createNotification(
          'error',
          'Error',
          err?.message || `Failed to load splitter page: ${routeKey}`,
        );
        this.isPageSplitterLoading.set(false);
      });
  }

  public closePageSplitter(): void {
    this.isPageSplitterVisible.set(false);
    this.pageSplitterConfig.set(null);
  }

  /**
   * Universal DOM click listener bound to the root element via Event Delegation.
   * Scrapes DOM nodes upwards to locate elements carrying `data-action` attributes and natively triggers them.
   * Builds the memory context containing state, root data, and explicit targets.
   * @param {Event} event - Native DOM mouse event.
   */
  public handleUserInteraction(event: Event): void {
    const target = event.target as HTMLElement;

    // Bubble up to find the closest interactive element mapping to an action
    const actionElement = target.closest('[data-action]') as HTMLElement;
    if (!actionElement) return;

    const actionName = actionElement.getAttribute('data-action');
    if (!actionName) return;

    const dataId = actionElement.getAttribute('data-id') || '';
    const targetId = actionElement.getAttribute('data-target');
    let targetValue = '';

    // If an explicit target ID was provided, scrape the current value from the target DOM input
    if (targetId) {
      // Use this component's ElementRef to search ONLY inside this specific instance, preventing ID collisions across multiple renderers
      const inputEl = this.el.nativeElement.querySelector(
        `[id="${targetId}"]`,
      ) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (inputEl) {
        targetValue = inputEl.value;
      }
    }

    // Map the collective state memory for variable interpolation mapping
    const context = {
      ...this.rootDataObject(),
      ...this.pageState(),
      pageState: this.pageState(), // CRITICAL: Explicitly embed pageState for {{pageState.counter}} resolution
      _dataId: dataId, // Internal tracking variable
      _targetValue: targetValue, // Internal tracking variable
      id: dataId, // Backwards compatibility for {{id}}
      targetValue: targetValue, // Backwards compatibility for {{targetValue}}
    };

    // Trigger all matching chained application events asynchronously
    this.triggerAppEvent(actionName, context);
  }

  /**
   * Resolves execution paths sequentially based on the declarative AppEvent configuration.
   * Interpolates data constraints securely using Handlebars variables natively before routing
   * to APIs, angular routers, or toasts. Triggers success or error paths recursively.
   * @param {string} eventName - The action identifier string.
   * @param {Record<string, any>} context - The synthesized state dictionary.
   */
  private async triggerAppEvent(
    eventName: string,
    context: Record<string, any>,
  ): Promise<void> {
    const eventsToRun = this.pageConfig().events?.filter(
      (e) => e.eventName === eventName,
    );
    if (!eventsToRun || eventsToRun.length === 0) {
      console.warn(
        `[DynamicPageRenderer] No event mapping found for data-action="${eventName}"`,
      );
      return;
    }

    for (const eventConfig of eventsToRun) {
      try {
        this.onEventTriggered.emit({
          eventName: eventConfig.eventName,
          context: { ...context },
          timestamp: new Date(),
          type: 'action_start',
        });

        if (eventConfig.type === 'endpoint' && eventConfig.payload.http) {
          const http = eventConfig.payload.http;
          const path = interpolateStringWithContext(http.path, context);
          const body = interpolateObjectWithContext(http.body, context);

          const response = await firstValueFrom(
            this.projectDataService.executeProjectApiCall(
              http.method,
              path,
              body,
            ),
          );

          // Retain the server response in context memory for sequential successor events to leverage
          context['$response'] = response;
          this.onEventTriggered.emit({
            eventName: eventConfig.eventName,
            response,
            timestamp: new Date(),
            type: 'endpoint_success',
          });
        } else if (
          eventConfig.type === 'navigate' &&
          eventConfig.payload.route
        ) {
          const route = interpolateStringWithContext(
            eventConfig.payload.route,
            context,
          );
          this.router.navigate([route]);
        } else if (
          eventConfig.type === 'showNotification' &&
          eventConfig.payload.notification
        ) {
          const notif = eventConfig.payload.notification;
          const msg = interpolateStringWithContext(notif.message, context);
          this.notificationService.createNotification(
            notif.type as any,
            'System Message',
            msg,
          );
        } else if (eventConfig.type === 'refreshData') {
          await this.refreshVariables(eventConfig.payload.variablesToRefresh);
        } else if (eventConfig.type === 'updateState') {
          // If a state update payload was explicitly provided, apply it first
          if (eventConfig.payload?.stateUpdate) {
            const updatedPairs = interpolateObjectWithContext(
              eventConfig.payload.stateUpdate,
              context,
            );
            this.pageState.update((state) => ({ ...state, ...updatedPairs }));

            // Execute the local execution context synchronously so subsequent chained events have the newest data
            Object.assign(context, updatedPairs);
            context['pageState'] = { ...context['pageState'], ...updatedPairs };
          }

          // CRITICAL FIX: After ANY state update, force JSONata recalculation natively in memory.
          // This allows an empty state payload (like the applyFilters click) to still successfully trigger UI filters!
          await this.recalculateTransformations();
        } else if (
          eventConfig.type === 'showPageModal' &&
          eventConfig.payload.pageModal
        ) {
          const pm = eventConfig.payload.pageModal;
          const resolvedRoute = interpolateStringWithContext(pm.route, context);
          const resolvedPayload = interpolateObjectWithContext(
            pm.payload,
            context,
          );
          const resolvedTitle = pm.title
            ? interpolateStringWithContext(pm.title, context)
            : undefined;

          this.openPageModal(
            resolvedRoute,
            resolvedPayload,
            resolvedTitle,
            pm.width,
          );
        } else if (
          eventConfig.type === 'showPageDrawer' &&
          eventConfig.payload.pageDrawer
        ) {
          const pd = eventConfig.payload.pageDrawer;
          const resolvedRoute = interpolateStringWithContext(pd.route, context);
          const resolvedPayload = interpolateObjectWithContext(
            pd.payload,
            context,
          );
          const resolvedTitle = pd.title
            ? interpolateStringWithContext(pd.title, context)
            : undefined;

          this.openPageDrawer(
            resolvedRoute,
            resolvedPayload,
            resolvedTitle,
            pd.placement,
            pd.size,
            pd.width,
          );
        } else if (
          eventConfig.type === 'showPageSplitter' &&
          eventConfig.payload.pageSplitter
        ) {
          const ps = eventConfig.payload.pageSplitter;
          const resolvedRoute = interpolateStringWithContext(ps.route, context);
          const resolvedPayload = interpolateObjectWithContext(
            ps.payload,
            context,
          );
          const resolvedTitle = ps.title
            ? interpolateStringWithContext(ps.title, context)
            : undefined;
          const resolvedIcon = ps.icon
            ? interpolateStringWithContext(ps.icon, context)
            : undefined;

          this.openPageSplitter(
            resolvedRoute,
            resolvedPayload,
            resolvedTitle,
            resolvedIcon,
            ps.defaultSize,
            ps.min,
            ps.max,
            ps.resizable,
            ps.layout,
          );
        }

        // Recursively walk through sequential execution chains upon absolute success
        if (
          eventConfig.onSuccessEvents &&
          eventConfig.onSuccessEvents.length > 0
        ) {
          for (const nextEvent of eventConfig.onSuccessEvents) {
            await this.triggerAppEvent(nextEvent, context);
          }
        }
      } catch (err: any) {
        console.error(
          `[DynamicPageRenderer] Action Execution Failed for event "${eventConfig.eventName}":`,
          err,
        );
        this.onEventTriggered.emit({
          eventName: eventConfig.eventName,
          error: err.message || String(err),
          timestamp: new Date(),
          type: 'action_error',
        });

        // Execute fallback operations or display visual fallback error toasts
        if (eventConfig.onErrorEvents && eventConfig.onErrorEvents.length > 0) {
          for (const errorEvent of eventConfig.onErrorEvents) {
            await this.triggerAppEvent(errorEvent, context);
          }
        } else {
          this.notificationService.createNotification(
            'error',
            'Action Failed',
            this.configTexts.actionFailed,
          );
        }
      }
    }
  }
}
